import {hideNotification, showNotification, updateNotification} from 'platform/components';
import {match} from 'ts-pattern';

import {useRef} from 'react';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {
  FINANCING_ENDING_BULK_OPERATION_STATE,
  financingEndingApi,
  type FinancingEndingBulkOperationResponseBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError, notificationTypes} from '@dms/shared';

import {type ApiError, useOnMount} from 'shared/web';

import {FINANCING_ENDING_POLLING_INTERVAL_MS} from '../constants/FINANCING_ENDING_POLLING_INTERVAL_MS';
import {openFinancingEndingOperationDialog} from '../utils/openFinancingEndingOperationDialog';

const TERMINAL_STATES: readonly string[] = [
  FINANCING_ENDING_BULK_OPERATION_STATE.DONE,
  FINANCING_ENDING_BULK_OPERATION_STATE.PARTIALLY_DONE,
  FINANCING_ENDING_BULK_OPERATION_STATE.FAILED,
  FINANCING_ENDING_BULK_OPERATION_STATE.RESOLVE_FAILED,
  FINANCING_ENDING_BULK_OPERATION_STATE.CANCELLED,
];

// The operation is gone or hidden from this user, so a later poll cannot succeed.
const NON_RETRYABLE_STATUSES: readonly number[] = [403, 404, 410];

const isNonRetryable = (error: ApiError) =>
  'status' in error &&
  typeof error.status === 'number' &&
  NON_RETRYABLE_STATUSES.includes(error.status);

// `doneCount` counts dispatched e-mails, not sent ones, so the toast reports processed rows only.
const getProgressMessage = (operation: FinancingEndingBulkOperationResponseBody) =>
  i18n.t('entity.businessCase.financingEnding.progress.counter', {
    done: operation.doneCount + operation.failedCount + operation.skippedCount,
    total: operation.totalCount,
  });

const getPollResultText = (state: string) =>
  match(state)
    .with(FINANCING_ENDING_BULK_OPERATION_STATE.RESOLVE_FAILED, () => ({
      title: i18n.t('entity.businessCase.financingEnding.notification.sendingFailedToStart.title'),
      message: i18n.t('entity.businessCase.financingEnding.notification.sendingFailedToStart.body'),
    }))
    .with(FINANCING_ENDING_BULK_OPERATION_STATE.CANCELLED, () => ({
      title: i18n.t('entity.businessCase.financingEnding.notification.sendingCancelled.title'),
      message: i18n.t('entity.businessCase.financingEnding.result.cancelled'),
    }))
    .otherwise(() => ({
      title: i18n.t('entity.businessCase.financingEnding.result.title'),
      message: i18n.t('entity.businessCase.financingEnding.result.finishedNoCounts'),
    }));

type OperationResult = {
  bulkOperationId: string;
  isSuccess: boolean;
  title: string;
  message: string;
};

type ResultSource = 'poll' | 'counts';

const getResultToastId = (bulkOperationId: string, source: ResultSource) =>
  `financingEndingResult-${bulkOperationId}-${source}`;

// A result stays until it is dismissed or its action is clicked.
const showResult = (
  result: OperationResult,
  source: ResultSource,
  openDetail: (bulkOperationId: string) => void
) => {
  const show = result.isSuccess ? showNotification.success : showNotification.warning;
  const id = getResultToastId(result.bulkOperationId, source);

  show(result.message, {
    id,
    title: result.title,
    layout: 'detailed',
    autoClose: false,
    actions: [
      {
        title: i18n.t('entity.businessCase.financingEnding.result.openDetail'),
        onClick: () => {
          hideNotification(id);
          openDetail(result.bulkOperationId);
        },
      },
    ],
  });
};

type UseFinancingEndingSendingProgressParams = {
  onOpenDetailByLink: (bulkOperationId: string) => void;
};

/**
 * Follows one bulk e-mail operation with a progress toast while the list page is mounted.
 *
 * Polling is driven from the submit handler rather than from `pollingInterval`, because every
 * response updates a toast, and a toast cannot be updated from render. A state this frontend does
 * not know counts as non-terminal: stopping on it would freeze the counter on exactly the states
 * nobody has taught the frontend about yet. A transient poll failure is reported once per
 * operation and polling goes on, so the toast keeps its last counter. A 403, 404 or 410 is reported
 * and ends the polling with the toast.
 *
 * The `sales-financing_ending_sending_finished` notification of an operation started here ends its
 * polling and shows the result with the counts of the notification. A run shows one result: the
 * counts replace a result the poll already showed without counts, and once the notification ended
 * the polling, the poll shows no result. Notifications of other operations are left to the
 * notification center.
 *
 * `openDetail` opens the operation dialog; a rerun it accepts is followed by `start`, which ends
 * the polling of any earlier operation. While the dialog of the followed operation is open, the
 * dialog polls the operation and this polling pauses, so one poller runs per operation. The polling
 * resumes once when the detail unmounts, also when a location change drops the dialog without a
 * close, unless a rerun, the notification, a stop or the unmount ended it. After the unmount,
 * `start` only shows that the operation runs in the background, without progress.
 *
 * The detail actions of the progress toast and, while the hook is mounted, of the result toasts
 * call `onOpenDetailByLink`, so the caller opens the detail the same way as its link does and a
 * reload keeps it open. A result toast outlives the list page; after the unmount its action opens
 * the detail directly, as no link opener is left to react.
 */
export function useFinancingEndingSendingProgress(params: UseFinancingEndingSendingProgressParams) {
  const [getBulkOperation] = financingEndingApi.useLazyGetFinancingEndingBulkOperationQuery();
  const activeOperationIdRef = useRef<string | null>(null);
  const startedOperationIdsRef = useRef(new Set<string>());
  const pollResultOperationIdsRef = useRef(new Set<string>());
  const runIdRef = useRef(0);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasReportedErrorRef = useRef(false);
  const isMountedRef = useRef(false);

  const stopPolling = () => {
    runIdRef.current += 1;

    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const stop = () => {
    stopPolling();

    if (activeOperationIdRef.current !== null) {
      hideNotification(activeOperationIdRef.current);
      activeOperationIdRef.current = null;
    }
  };

  useOnMount(
    () => {
      isMountedRef.current = true;
    },
    () => {
      isMountedRef.current = false;
      stop();
    }
  );

  const poll = (bulkOperationId: string, runId: number) => {
    const isActive = () => runIdRef.current === runId;

    const scheduleNext = () => {
      timeoutIdRef.current = setTimeout(
        () => poll(bulkOperationId, runId),
        FINANCING_ENDING_POLLING_INTERVAL_MS
      );
    };

    // Two handlers, not `.catch`: a throw while showing a result must not count as a poll error and
    // poll again.
    getBulkOperation({bulkOperationId})
      .unwrap()
      .then(
        (operation) => {
          if (!isActive()) {
            return;
          }

          if (TERMINAL_STATES.includes(operation.state)) {
            stop();
            showPollResult({
              bulkOperationId: operation.bulkOperationId,
              isSuccess: operation.state === FINANCING_ENDING_BULK_OPERATION_STATE.DONE,
              ...getPollResultText(operation.state),
            });
            return;
          }

          updateNotification({id: bulkOperationId, message: getProgressMessage(operation)});
          scheduleNext();
        },
        (error: ApiError) => {
          if (!isActive()) {
            return;
          }

          if (isNonRetryable(error)) {
            handleApiError(error);
            stop();
            return;
          }

          if (!hasReportedErrorRef.current) {
            hasReportedErrorRef.current = true;
            handleApiError(error);
          }

          scheduleNext();
        }
      );
  };

  const start = (bulkOperationId: string) => {
    // A rerun from a toast that outlived the list: no unmount would end this polling, so only
    // confirm that the operation runs.
    if (!isMountedRef.current) {
      showNotification.info(i18n.t('entity.businessCase.financingEnding.progress.background'), {
        title: i18n.t('entity.businessCase.financingEnding.progress.title'),
      });
      return;
    }

    stop();
    activeOperationIdRef.current = bulkOperationId;
    hasReportedErrorRef.current = false;
    startedOperationIdsRef.current.add(bulkOperationId);

    showNotification.info(undefined, {
      id: bulkOperationId,
      title: i18n.t('entity.businessCase.financingEnding.progress.title'),
      secondaryMessage: i18n.t('entity.businessCase.financingEnding.progress.background'),
      layout: 'detailed',
      isLoading: true,
      autoClose: false,
      actions: [
        {
          title: i18n.t('entity.businessCase.financingEnding.result.openDetail'),
          onClick: () => params.onOpenDetailByLink(bulkOperationId),
        },
      ],
    });

    poll(bulkOperationId, runIdRef.current);
  };

  const openDetail = (bulkOperationId: string, onCloseComplete?: () => void) => {
    const isFollowed = activeOperationIdRef.current === bulkOperationId;

    if (isFollowed) {
      stopPolling();
    }

    const pausedRunId = runIdRef.current;
    let hasResumed = false;

    // A resumed poll keeps the run id, so a second resume would start a second loop.
    const resume = () => {
      if (hasResumed) {
        return;
      }

      hasResumed = true;

      if (isFollowed && runIdRef.current === pausedRunId) {
        poll(bulkOperationId, pausedRunId);
      }
    };

    openFinancingEndingOperationDialog({
      bulkOperationId,
      onRerunAccepted: start,
      onUnmount: resume,
      onCloseComplete,
    });
  };

  const openResultDetail = (bulkOperationId: string) =>
    isMountedRef.current ? params.onOpenDetailByLink(bulkOperationId) : openDetail(bulkOperationId);

  const showPollResult = (result: OperationResult) => {
    pollResultOperationIdsRef.current.add(result.bulkOperationId);
    showResult(result, 'poll', openResultDetail);
  };

  const showCountsResult = (result: OperationResult) => {
    if (pollResultOperationIdsRef.current.delete(result.bulkOperationId)) {
      hideNotification(getResultToastId(result.bulkOperationId, 'poll'));
    }
    showResult(result, 'counts', openResultDetail);
  };

  useSubscribeToNotificationsQuery((notification) => {
    if (
      notification.type !== notificationTypes.FINANCING_ENDING_SENDING_FINISHED ||
      !startedOperationIdsRef.current.has(notification.payload.bulkOperationId)
    ) {
      return;
    }

    const {bulkOperationId, sentCount, skippedCount, failedCount} = notification.payload;
    startedOperationIdsRef.current.delete(bulkOperationId);

    if (activeOperationIdRef.current === bulkOperationId) {
      stop();
    }

    showCountsResult({
      bulkOperationId,
      isSuccess: failedCount === 0,
      title: i18n.t('entity.businessCase.financingEnding.result.title'),
      message: i18n.t('entity.businessCase.financingEnding.result.counts', {
        sent: sentCount,
        skipped: skippedCount,
        failed: failedCount,
      }),
    });
  });

  return {start, openDetail};
}
